import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  type GeckoCircumferentialWeld,
  type GeckoComponentModel,
  type GeckoComponentRect,
  type GeckoConeCircumferentialWeld,
  type GeckoConeRadialWeld,
  type GeckoFixedSensor,
  type GeckoFeature,
  type GeckoGenericHeadCircumferentialWeld,
  type GeckoGenericHeadRadialWeld,
  type GeckoHorizontalWeld,
  type GeckoLegacyBoilerWall,
  type GeckoLegacyBoilerWalls,
  type GeckoLegacyCone,
  type GeckoLeakDetectionValve,
  type GeckoManway,
  type GeckoNozzle,
  type GeckoPlate,
  type GeckoPolygonalPlate,
  type GeckoRectangularManway,
  type GeckoSeamWeld,
  type GeckoShipDeckBitt,
  type GeckoShipDeckBulkhead,
  type GeckoShipDeckChock,
  type GeckoShipDeckCoaming,
  type GeckoShipDeckCompartment,
  type GeckoShipDeckDoor,
  type GeckoShipDeckDrain,
  type GeckoShipDeckHatch,
  type GeckoShipDeckLadder,
  type GeckoShipDeckLocker,
  type GeckoTankFlatRoof,
  type GeckoVerticalTankShell,
  type GeckoVerticalWeld,
} from 'types'

import { GetSectionable } from '../../systems/clipConfig'
import {
  FEATURE_OUTLINE_COLOR,
  SECTION_LINE_COLOR,
  WELD_LINE_COLOR,
} from './constants'
import { resolveValueDisplayUnit } from './data'
import { formatTick } from './ticks'
import type {
  BoilerWall,
  CourseWithThickness,
  NormalizedBounds,
  OverlayLabelPrimitive,
  OverlayLinePrimitive,
  OverlayShapePrimitive,
  OverlayPrimitives,
  ReportComponentOverlayMode,
  ReportLabelMode,
  ReportHeatmapRequest,
} from './types'
import {
  OverlayLabelAnchor as LabelAnchor,
  OverlayLabelPlacement as LabelPlacement,
  ReportComponentOverlayMode as ComponentOverlayMode,
  ReportLabelMode as LabelMode,
} from './types'

const NOZZLE_SCALE_THRESHOLD = GeckoMeasurement.CreateFromValue(
  10,
  GeckoMeasurementUnit.Inches
)
const FEATURE_OUTLINE_LINE_WIDTH = 1.5
const PRIMARY_FEATURE_OUTLINE_LINE_WIDTH = 2.5
const FEATURE_REPAD_SCALE = 1.5

export function buildReportOverlayPrimitives(
  request: Pick<
    ReportHeatmapRequest,
    | 'componentModel'
    | 'componentOverlayMode'
    | 'includeComponentOverlay'
    | 'labelMode'
    | 'overlayPrimitives'
  >,
  bounds: NormalizedBounds
): OverlayPrimitives {
  const linePrimitives: OverlayLinePrimitive[] = []
  const shapePrimitives: OverlayShapePrimitive[] = []
  const labelPrimitives: OverlayLabelPrimitive[] = []
  const componentBounds = bounds.rect
  const sectionable = GetSectionable(request.componentModel)

  if (request.includeComponentOverlay !== false && sectionable) {
    const seenBoundaries = new Set<string>()
    sectionable.sections.forEach((section) => {
      if (sectionable.splitByX) {
        const xStart = toCoordinateValue(section.min, componentBounds.x.unit)
        const xEnd = toCoordinateValue(section.max, componentBounds.x.unit)
        ;[xStart, xEnd].forEach((x) => {
          const key = `vx:${roundForKey(x)}`
          if (seenBoundaries.has(key)) return
          seenBoundaries.add(key)
          linePrimitives.push({
            x1: x,
            y1: componentBounds.y.min.toNumber(componentBounds.y.unit),
            x2: x,
            y2: componentBounds.y.max.toNumber(componentBounds.y.unit),
            strokeStyle: SECTION_LINE_COLOR,
            lineWidth: 2,
          })
        })
      } else {
        const yStart = toCoordinateValue(section.min, componentBounds.y.unit)
        const yEnd = toCoordinateValue(section.max, componentBounds.y.unit)
        ;[yStart, yEnd].forEach((y) => {
          const key = `hy:${roundForKey(y)}`
          if (seenBoundaries.has(key)) return
          seenBoundaries.add(key)
          linePrimitives.push({
            x1: componentBounds.x.min.toNumber(componentBounds.x.unit),
            y1: y,
            x2: componentBounds.x.max.toNumber(componentBounds.x.unit),
            y2: y,
            strokeStyle: SECTION_LINE_COLOR,
            lineWidth: 2,
          })
        })
      }
    })

    if (request.labelMode !== LabelMode.None) {
      appendSectionLabels(
        request.componentModel,
        componentBounds,
        labelPrimitives
      )
    }
  }

  if (request.includeComponentOverlay !== false) {
    appendFeatureOverlayPrimitives(
      request.componentModel,
      componentBounds,
      request.labelMode,
      request.componentOverlayMode ?? ComponentOverlayMode.Standard,
      linePrimitives,
      shapePrimitives,
      labelPrimitives
    )
  }

  if (request.overlayPrimitives) {
    linePrimitives.push(...request.overlayPrimitives.linePrimitives)
    shapePrimitives.push(...request.overlayPrimitives.shapePrimitives)
    labelPrimitives.push(...request.overlayPrimitives.labelPrimitives)
  }

  return {
    linePrimitives,
    shapePrimitives,
    labelPrimitives,
  }
}

function appendSectionLabels(
  componentModel: GeckoComponentModel,
  componentBounds: GeckoComponentRect,
  labelPrimitives: OverlayLabelPrimitive[]
) {
  const xMin = componentBounds.x.min.toNumber(componentBounds.x.unit)
  const xMax = componentBounds.x.max.toNumber(componentBounds.x.unit)
  const yMin = componentBounds.y.min.toNumber(componentBounds.y.unit)
  const yMax = componentBounds.y.max.toNumber(componentBounds.y.unit)
  const xInset = (xMax - xMin) * 0.04
  const yInset = (yMax - yMin) * 0.08

  switch (componentModel.type) {
    case GeckoComponentModelType.VerticalTankShell: {
      ;(componentModel as GeckoVerticalTankShell).coursesArray.forEach(
        (course) => {
          labelPrimitives.push({
            x: xMax - xInset,
            y:
              (toCoordinateValue(
                course.geometry.y.min,
                componentBounds.y.unit
              ) +
                toCoordinateValue(
                  course.geometry.y.max,
                  componentBounds.y.unit
                )) /
              2,
            lines: createCourseLabelLines(course),
            anchor: LabelAnchor.Left,
            placement: LabelPlacement.ShellCourseList,
            slotStartY: toCoordinateValue(
              course.geometry.y.min,
              componentBounds.y.unit
            ),
            slotEndY: toCoordinateValue(
              course.geometry.y.max,
              componentBounds.y.unit
            ),
            priority: 1,
            font: '12px Arial',
            lineHeight: 13,
          })
        }
      )
      break
    }
    case GeckoComponentModelType.LegacyCone: {
      ;(componentModel as GeckoLegacyCone).coursesArray.forEach((course) => {
        labelPrimitives.push({
          x: xMax - xInset,
          y:
            (toCoordinateValue(course.geometry.y.min, componentBounds.y.unit) +
              toCoordinateValue(
                course.geometry.y.max,
                componentBounds.y.unit
              )) /
            2,
          lines: createCourseLabelLines(course),
          anchor: LabelAnchor.Right,
          placement: LabelPlacement.CourseGutter,
          slotStartY: toCoordinateValue(
            course.geometry.y.min,
            componentBounds.y.unit
          ),
          slotEndY: toCoordinateValue(
            course.geometry.y.max,
            componentBounds.y.unit
          ),
          priority: 1,
          font: 'bold 12px Arial',
          lineHeight: 14,
        })
      })
      break
    }
    case GeckoComponentModelType.LegacyBoilerWalls: {
      ;(componentModel as GeckoLegacyBoilerWalls).wallsArray.forEach((wall) => {
        const x =
          (toCoordinateValue(wall.geometry.x.min, componentBounds.x.unit) +
            toCoordinateValue(wall.geometry.x.max, componentBounds.x.unit)) /
          2
        labelPrimitives.push({
          x,
          y: yMax - yInset,
          lines: [wall.name],
          anchor: LabelAnchor.Bottom,
          placement: LabelPlacement.Anchored,
          priority: 1,
          font: 'bold 24px Arial',
          lineHeight: 28,
        })

        const tubeRange = formatTubeRange(wall)
        if (tubeRange) {
          labelPrimitives.push({
            x,
            y: yMin + yInset,
            lines: [tubeRange],
            anchor: LabelAnchor.Top,
            placement: LabelPlacement.Anchored,
            priority: 1,
            font: '20px Arial',
            lineHeight: 24,
          })
        }
      })
      break
    }
    case GeckoComponentModelType.TankFlatRoof: {
      labelPrimitives.push({
        x: xMax - xInset,
        y: yMax - yInset,
        lines: [(componentModel as GeckoTankFlatRoof).name],
        anchor: LabelAnchor.Right,
        placement: LabelPlacement.CourseGutter,
        slotStartY: yMin,
        slotEndY: yMax,
        priority: 1,
        font: 'bold 12px Arial',
        lineHeight: 14,
      })
      break
    }
  }
}

function appendFeatureOverlayPrimitives(
  componentModel: GeckoComponentModel,
  componentBounds: GeckoComponentRect,
  labelMode: ReportLabelMode,
  componentOverlayMode: ReportComponentOverlayMode,
  linePrimitives: OverlayLinePrimitive[],
  shapePrimitives: OverlayShapePrimitive[],
  labelPrimitives: OverlayLabelPrimitive[]
) {
  const features = hasReportOverlayFeatures(componentModel)
    ? Array.from(componentModel.features.values())
    : []

  features.forEach((feature) => {
    const featureType = getFeatureType(feature)

    switch (featureType) {
      case GeckoFeatureModelType.Plate: {
        if (componentOverlayMode !== ComponentOverlayMode.Full) {
          break
        }
        const plate = feature as GeckoPlate
        appendPlateOutline(plate, componentBounds, linePrimitives)
        if (labelMode !== LabelMode.None) {
          const label = createPlateFeatureLabel(plate, componentBounds)
          if (label) {
            labelPrimitives.push(label)
          }
        }
        break
      }
      case GeckoFeatureModelType.PolygonalPlate: {
        if (componentOverlayMode !== ComponentOverlayMode.Full) {
          break
        }
        const plate = feature as GeckoPolygonalPlate
        appendPolygonalPlateOutline(plate, componentBounds, linePrimitives)
        if (labelMode !== LabelMode.None) {
          const label = createPolygonalPlateFeatureLabel(
            plate,
            componentBounds
          )
          if (label) {
            labelPrimitives.push(label)
          }
        }
        break
      }
      case GeckoFeatureModelType.HorizontalWeld:
      case GeckoFeatureModelType.VerticalWeld:
      case GeckoFeatureModelType.SeamWeld:
      case GeckoFeatureModelType.CircumferentialWeld:
      case GeckoFeatureModelType.GenericHeadCircumferentialWeld:
      case GeckoFeatureModelType.GenericHeadRadialWeld:
      case GeckoFeatureModelType.ConeCircumferentialWeld:
      case GeckoFeatureModelType.ConeRadialWeld: {
        if (
          componentOverlayMode !== ComponentOverlayMode.Full &&
          featureType !== GeckoFeatureModelType.HorizontalWeld &&
          featureType !== GeckoFeatureModelType.VerticalWeld &&
          featureType !== GeckoFeatureModelType.ConeCircumferentialWeld &&
          featureType !== GeckoFeatureModelType.ConeRadialWeld
        ) {
          break
        }
        const weld = feature as ReportWeldFeature
        appendWeldLine(weld, componentBounds, linePrimitives)
        if (
          componentOverlayMode === ComponentOverlayMode.Full &&
          labelMode !== LabelMode.None
        ) {
          const label = createWeldFeatureLabel(weld, componentBounds)
          if (label) {
            labelPrimitives.push(label)
          }
        }
        break
      }
      case GeckoFeatureModelType.Nozzle:
      case GeckoFeatureModelType.Manway:
      case GeckoFeatureModelType.RectangularManway:
      case GeckoFeatureModelType.LeakDetectionValve: {
        if (
          componentOverlayMode !== ComponentOverlayMode.Full &&
          featureType === GeckoFeatureModelType.LeakDetectionValve
        ) {
          break
        }
        const pointFeature = feature as ReportPointFeature
        appendPointFeatureShape(pointFeature, componentBounds, shapePrimitives)
        if (labelMode !== LabelMode.None) {
          const label = createPointFeatureLabel(pointFeature, componentBounds)
          if (label) {
            labelPrimitives.push(label)
          }
        }
        break
      }
      case GeckoFeatureModelType.FixedSensor: {
        if (labelMode !== LabelMode.None) {
          const label = createPointFeatureLabel(
            feature as GeckoFixedSensor,
            componentBounds
          )
          if (label) {
            labelPrimitives.push(label)
          }
        }
        break
      }
      case GeckoFeatureModelType.ShipDeckCompartment: {
        if (componentOverlayMode !== ComponentOverlayMode.Full) {
          break
        }
        const compartment = feature as GeckoShipDeckCompartment
        appendCoordinatePolygonOutline(
          compartment.geometry,
          componentBounds,
          linePrimitives
        )
        if (labelMode !== LabelMode.None) {
          const label = createCoordinateFeatureLabel(
            compartment.name,
            compartment.geometry,
            componentBounds
          )
          if (label) {
            labelPrimitives.push(label)
          }
        }
        break
      }
      case GeckoFeatureModelType.ShipDeckLocker:
      case GeckoFeatureModelType.ShipDeckDrain: {
        if (componentOverlayMode !== ComponentOverlayMode.Full) {
          break
        }
        const polygonalPlateFeature = feature as ReportPolygonalPlateFeature
        appendPolygonalPlateOutline(
          polygonalPlateFeature.polygonalPlate,
          componentBounds,
          linePrimitives
        )
        if (labelMode !== LabelMode.None) {
          const label = createCoordinateFeatureLabel(
            polygonalPlateFeature.name,
            polygonalPlateFeature.polygonalPlate.geometry,
            componentBounds
          )
          if (label) {
            labelPrimitives.push(label)
          }
        }
        break
      }
      case GeckoFeatureModelType.ShipDeckCoaming:
      case GeckoFeatureModelType.ShipDeckBulkhead:
      case GeckoFeatureModelType.ShipDeckDoor:
      case GeckoFeatureModelType.ShipDeckLadder: {
        if (componentOverlayMode !== ComponentOverlayMode.Full) {
          break
        }
        const pathFeature = feature as ReportCoordinatePathFeature
        appendCoordinatePath(
          pathFeature.coordinates,
          componentBounds,
          linePrimitives
        )
        if (labelMode !== LabelMode.None) {
          const label = createCoordinateFeatureLabel(
            pathFeature.name,
            pathFeature.coordinates,
            componentBounds
          )
          if (label) {
            labelPrimitives.push(label)
          }
        }
        break
      }
      case GeckoFeatureModelType.ShipDeckHatch: {
        if (componentOverlayMode !== ComponentOverlayMode.Full) {
          break
        }
        const hatch = feature as GeckoShipDeckHatch
        appendShipDeckHatchShape(hatch, componentBounds, shapePrimitives)
        if (labelMode !== LabelMode.None) {
          const label = createNamedPointLabel(
            hatch.name,
            hatch.geometry.x,
            hatch.geometry.y,
            componentBounds
          )
          if (label) {
            labelPrimitives.push(label)
          }
        }
        break
      }
      case GeckoFeatureModelType.ShipDeckBitt:
      case GeckoFeatureModelType.ShipDeckChock: {
        if (labelMode !== LabelMode.None) {
          const pointFeature = feature as ReportNamedPointFeature
          const label = createNamedPointLabel(
            pointFeature.name,
            pointFeature.geometry.x,
            pointFeature.geometry.y,
            componentBounds
          )
          if (label) {
            labelPrimitives.push(label)
          }
        }
        break
      }
      case GeckoFeatureModelType.LegacyBoilerWall:
        appendBoilerWallOutline(
          feature as GeckoLegacyBoilerWall,
          componentBounds,
          linePrimitives
        )
        break
    }
  })
}

type ReportOverlayComponentModel = GeckoComponentModel & {
  features: {
    values(): IterableIterator<GeckoFeature>
  }
}

function hasReportOverlayFeatures(
  componentModel: GeckoComponentModel
): componentModel is ReportOverlayComponentModel {
  return 'features' in componentModel
}

function getFeatureType(feature: GeckoFeature) {
  return 'type' in feature
    ? (feature.type as GeckoFeatureModelType)
    : undefined
}

type ReportWeldFeature =
  | GeckoHorizontalWeld
  | GeckoVerticalWeld
  | GeckoSeamWeld
  | GeckoCircumferentialWeld
  | GeckoGenericHeadCircumferentialWeld
  | GeckoGenericHeadRadialWeld
  | GeckoConeCircumferentialWeld
  | GeckoConeRadialWeld

type ReportPointFeature =
  | GeckoNozzle
  | GeckoManway
  | GeckoRectangularManway
  | GeckoLeakDetectionValve

type ReportPolygonalPlateFeature = GeckoShipDeckLocker | GeckoShipDeckDrain

type ReportCoordinatePathFeature =
  | GeckoShipDeckCoaming
  | GeckoShipDeckBulkhead
  | GeckoShipDeckDoor
  | GeckoShipDeckLadder

type ReportNamedPointFeature = GeckoShipDeckBitt | GeckoShipDeckChock

function appendPlateOutline(
  plate: GeckoPlate,
  componentBounds: GeckoComponentRect,
  linePrimitives: OverlayLinePrimitive[]
) {
  const geometry = plate.geometry.project(componentBounds.space)

  appendRectOutline(
    geometry.x.min.num,
    geometry.y.min.num,
    geometry.x.max.num,
    geometry.y.max.num,
    linePrimitives
  )
}

function appendPolygonalPlateOutline(
  plate: GeckoPolygonalPlate,
  componentBounds: GeckoComponentRect,
  linePrimitives: OverlayLinePrimitive[]
) {
  const points = plate.geometry
    .map((point) => ({
      x: toCoordinateValue(point.x, componentBounds.x.unit),
      y: toCoordinateValue(point.y, componentBounds.y.unit),
    }))
    .filter(
      (point) => isFiniteCoordinate(point.x) && isFiniteCoordinate(point.y)
    )

  if (points.length < 2) {
    return
  }

  points.forEach((point, index) => {
    const nextPoint = points[(index + 1) % points.length]
    linePrimitives.push({
      x1: point.x,
      y1: point.y,
      x2: nextPoint.x,
      y2: nextPoint.y,
      strokeStyle: FEATURE_OUTLINE_COLOR,
      lineWidth: FEATURE_OUTLINE_LINE_WIDTH,
    })
  })
}

type CoordinatePair = {
  readonly x: GeckoMeasurement
  readonly y: GeckoMeasurement
}

function appendCoordinatePolygonOutline(
  coordinates: readonly CoordinatePair[],
  componentBounds: GeckoComponentRect,
  linePrimitives: OverlayLinePrimitive[]
) {
  const points = projectCoordinatePairs(coordinates, componentBounds)

  if (points.length < 2) {
    return
  }

  points.forEach((point, index) => {
    const nextPoint = points[(index + 1) % points.length]
    linePrimitives.push({
      x1: point.x,
      y1: point.y,
      x2: nextPoint.x,
      y2: nextPoint.y,
      strokeStyle: FEATURE_OUTLINE_COLOR,
      lineWidth: FEATURE_OUTLINE_LINE_WIDTH,
    })
  })
}

function appendCoordinatePath(
  coordinates: readonly CoordinatePair[],
  componentBounds: GeckoComponentRect,
  linePrimitives: OverlayLinePrimitive[]
) {
  const points = projectCoordinatePairs(coordinates, componentBounds)

  if (points.length < 2) {
    return
  }

  for (let index = 1; index < points.length; index++) {
    const point = points[index - 1]
    const nextPoint = points[index]
    linePrimitives.push({
      x1: point.x,
      y1: point.y,
      x2: nextPoint.x,
      y2: nextPoint.y,
      strokeStyle: FEATURE_OUTLINE_COLOR,
      lineWidth: FEATURE_OUTLINE_LINE_WIDTH,
    })
  }
}

function projectCoordinatePairs(
  coordinates: readonly CoordinatePair[],
  componentBounds: GeckoComponentRect
) {
  return coordinates
    .map((point) => ({
      x: toCoordinateValue(point.x, componentBounds.x.unit),
      y: toCoordinateValue(point.y, componentBounds.y.unit),
    }))
    .filter(
      (point) => isFiniteCoordinate(point.x) && isFiniteCoordinate(point.y)
    )
}

function appendRectOutline(
  xMin: number,
  yMin: number,
  xMax: number,
  yMax: number,
  linePrimitives: OverlayLinePrimitive[]
) {
  if (
    !isFiniteCoordinate(xMin) ||
    !isFiniteCoordinate(xMax) ||
    !isFiniteCoordinate(yMin) ||
    !isFiniteCoordinate(yMax)
  ) {
    return
  }

  linePrimitives.push(
    {
      x1: xMin,
      y1: yMin,
      x2: xMax,
      y2: yMin,
      strokeStyle: FEATURE_OUTLINE_COLOR,
      lineWidth: FEATURE_OUTLINE_LINE_WIDTH,
    },
    {
      x1: xMax,
      y1: yMin,
      x2: xMax,
      y2: yMax,
      strokeStyle: FEATURE_OUTLINE_COLOR,
      lineWidth: FEATURE_OUTLINE_LINE_WIDTH,
    },
    {
      x1: xMax,
      y1: yMax,
      x2: xMin,
      y2: yMax,
      strokeStyle: FEATURE_OUTLINE_COLOR,
      lineWidth: FEATURE_OUTLINE_LINE_WIDTH,
    },
    {
      x1: xMin,
      y1: yMax,
      x2: xMin,
      y2: yMin,
      strokeStyle: FEATURE_OUTLINE_COLOR,
      lineWidth: FEATURE_OUTLINE_LINE_WIDTH,
    }
  )
}

function appendWeldLine(
  weld: ReportWeldFeature,
  componentBounds: GeckoComponentRect,
  linePrimitives: OverlayLinePrimitive[]
) {
  linePrimitives.push({
    x1: toCoordinateValue(weld.feature_geometry.x.min, componentBounds.x.unit),
    y1: toCoordinateValue(weld.feature_geometry.y.min, componentBounds.y.unit),
    x2: toCoordinateValue(weld.feature_geometry.x.max, componentBounds.x.unit),
    y2: toCoordinateValue(weld.feature_geometry.y.max, componentBounds.y.unit),
    strokeStyle: WELD_LINE_COLOR,
    lineWidth: FEATURE_OUTLINE_LINE_WIDTH,
  })
}

function appendBoilerWallOutline(
  wall: GeckoLegacyBoilerWall,
  componentBounds: GeckoComponentRect,
  linePrimitives: OverlayLinePrimitive[]
) {
  const xMin = toCoordinateValue(wall.geometry.x.min, componentBounds.x.unit)
  const xMax = toCoordinateValue(wall.geometry.x.max, componentBounds.x.unit)
  const yMin = toCoordinateValue(wall.geometry.y.min, componentBounds.y.unit)
  const yMax = toCoordinateValue(wall.geometry.y.max, componentBounds.y.unit)
  linePrimitives.push(
    {
      x1: xMin,
      y1: yMin,
      x2: xMax,
      y2: yMin,
      strokeStyle: SECTION_LINE_COLOR,
      lineWidth: FEATURE_OUTLINE_LINE_WIDTH,
    },
    {
      x1: xMin,
      y1: yMax,
      x2: xMax,
      y2: yMax,
      strokeStyle: SECTION_LINE_COLOR,
      lineWidth: FEATURE_OUTLINE_LINE_WIDTH,
    }
  )
}

function appendPointFeatureShape(
  feature:
    | GeckoNozzle
    | GeckoManway
    | GeckoRectangularManway
    | GeckoLeakDetectionValve,
  componentBounds: GeckoComponentRect,
  shapePrimitives: OverlayShapePrimitive[]
) {
  switch (feature.type) {
    case GeckoFeatureModelType.Nozzle:
      appendCircularFeatureShape(
        feature,
        getNozzleFeatureRadii(feature),
        componentBounds,
        shapePrimitives
      )
      break
    case GeckoFeatureModelType.Manway:
      appendCircularFeatureShape(
        feature,
        getManwayFeatureRadii(feature),
        componentBounds,
        shapePrimitives
      )
      break
    case GeckoFeatureModelType.RectangularManway:
      appendRectangularManwayShape(feature, componentBounds, shapePrimitives)
      break
    case GeckoFeatureModelType.LeakDetectionValve:
      appendCircularFeatureShape(
        feature,
        [feature.radius],
        componentBounds,
        shapePrimitives
      )
      break
  }
}

function appendCircularFeatureShape(
  feature: GeckoNozzle | GeckoManway | GeckoLeakDetectionValve,
  radii: GeckoMeasurement[],
  componentBounds: GeckoComponentRect,
  shapePrimitives: OverlayShapePrimitive[]
) {
  const x = toCoordinateValue(feature.geometry.x, componentBounds.x.unit)
  const y = toCoordinateValue(feature.geometry.y, componentBounds.y.unit)
  const seenRadii = new Set<string>()

  radii.forEach((radius, index) => {
    const radiusX = toCoordinateValue(radius, componentBounds.x.unit)
    const radiusY = toCoordinateValue(radius, componentBounds.y.unit)

    if (radiusX <= 0 || radiusY <= 0) {
      return
    }

    const key = `${roundForKey(radiusX)}:${roundForKey(radiusY)}`
    if (seenRadii.has(key)) {
      return
    }
    seenRadii.add(key)

    shapePrimitives.push({
      kind: 'circle',
      x,
      y,
      radiusX,
      radiusY,
      strokeStyle: FEATURE_OUTLINE_COLOR,
      lineWidth:
        index === 0
          ? PRIMARY_FEATURE_OUTLINE_LINE_WIDTH
          : FEATURE_OUTLINE_LINE_WIDTH,
    })
  })
}

function appendRectangularManwayShape(
  feature: GeckoRectangularManway,
  componentBounds: GeckoComponentRect,
  shapePrimitives: OverlayShapePrimitive[]
) {
  if (!feature.width || !feature.height) {
    return
  }

  const flangeWidth = feature.width.plus(
    GeckoMeasurement.CreateFromValue(0.025, feature.width.unit)
  )
  const flangeHeight = feature.height.plus(
    GeckoMeasurement.CreateFromValue(0.025, feature.height.unit)
  )
  const sizes = [
    {
      width: feature.width.scale(FEATURE_REPAD_SCALE),
      height: feature.height.scale(FEATURE_REPAD_SCALE),
      lineWidth: PRIMARY_FEATURE_OUTLINE_LINE_WIDTH,
    },
    {
      width: flangeWidth,
      height: flangeHeight,
      lineWidth: FEATURE_OUTLINE_LINE_WIDTH,
    },
    {
      width: feature.width,
      height: feature.height,
      lineWidth: FEATURE_OUTLINE_LINE_WIDTH,
    },
  ]
  const x = toCoordinateValue(feature.geometry.x, componentBounds.x.unit)
  const y = toCoordinateValue(feature.geometry.y, componentBounds.y.unit)
  const seenSizes = new Set<string>()

  sizes.forEach(({ width, height, lineWidth }) => {
    const shapeWidth = toCoordinateValue(width, componentBounds.x.unit)
    const shapeHeight = toCoordinateValue(height, componentBounds.y.unit)

    if (shapeWidth <= 0 || shapeHeight <= 0) {
      return
    }

    const key = `${roundForKey(shapeWidth)}:${roundForKey(shapeHeight)}`
    if (seenSizes.has(key)) {
      return
    }
    seenSizes.add(key)

    shapePrimitives.push({
      kind: 'rectangle',
      x,
      y,
      width: shapeWidth,
      height: shapeHeight,
      strokeStyle: FEATURE_OUTLINE_COLOR,
      lineWidth,
    })
  })
}

function appendShipDeckHatchShape(
  feature: GeckoShipDeckHatch,
  componentBounds: GeckoComponentRect,
  shapePrimitives: OverlayShapePrimitive[]
) {
  const x = toCoordinateValue(feature.geometry.x, componentBounds.x.unit)
  const y = toCoordinateValue(feature.geometry.y, componentBounds.y.unit)
  const width = toCoordinateValue(feature.width, componentBounds.x.unit)
  const height = toCoordinateValue(feature.height, componentBounds.y.unit)

  if (
    !isFiniteCoordinate(x) ||
    !isFiniteCoordinate(y) ||
    width <= 0 ||
    height <= 0
  ) {
    return
  }

  shapePrimitives.push({
    kind: 'rectangle',
    x,
    y,
    width,
    height,
    strokeStyle: FEATURE_OUTLINE_COLOR,
    lineWidth: FEATURE_OUTLINE_LINE_WIDTH,
  })
}

function getNozzleFeatureRadii(feature: GeckoNozzle) {
  const scale = getNozzleFeatureScale(feature.radius)
  const repadRadius = feature.radius.plus(
    GeckoMeasurement.CreateFromValue(0.1, feature.radius.unit).scale(scale)
  )
  const flangeRadius = feature.radius.plus(
    GeckoMeasurement.CreateFromValue(0.025, feature.radius.unit).scale(scale)
  )

  return [repadRadius, flangeRadius, feature.radius]
}

function getManwayFeatureRadii(feature: GeckoManway) {
  const repadRadius =
    feature.repadDiameter?.scale(0.5) ??
    feature.radius.scale(FEATURE_REPAD_SCALE)
  const flangeRadius = feature.radius.plus(
    GeckoMeasurement.CreateFromValue(0.025, feature.radius.unit)
  )

  return [repadRadius, flangeRadius, feature.radius]
}

function getNozzleFeatureScale(radius: GeckoMeasurement) {
  if (radius.gte(NOZZLE_SCALE_THRESHOLD)) {
    return 1
  }

  return (
    radius.toNumber(NOZZLE_SCALE_THRESHOLD.unit) /
    NOZZLE_SCALE_THRESHOLD.toNumber(NOZZLE_SCALE_THRESHOLD.unit)
  )
}

function createPointFeatureLabel(
  feature:
    | GeckoNozzle
    | GeckoManway
    | GeckoRectangularManway
    | GeckoLeakDetectionValve
    | GeckoFixedSensor,
  componentBounds: GeckoComponentRect
) {
  const name = 'name' in feature ? feature.name : undefined
  if (!name) {
    return undefined
  }

  const x = toCoordinateValue(feature.geometry.x, componentBounds.x.unit)
  const y = toCoordinateValue(feature.geometry.y, componentBounds.y.unit)
  const offset = getPointFeatureLabelOffset(feature, componentBounds.y.unit)
  const yMax = componentBounds.y.max.toNumber(componentBounds.y.unit)
  const remainingHeight = yMax - (y + offset)
  const componentHeight = componentBounds.height.toNumber(
    componentBounds.y.unit
  )
  const placeAbove = remainingHeight > componentHeight * 0.1

  return {
    x,
    y: placeAbove ? y + offset : y - offset,
    lines: [name],
    anchor: placeAbove ? LabelAnchor.Bottom : LabelAnchor.Top,
    placement: LabelPlacement.Anchored,
    priority: 2,
    font: '12px Arial',
    lineHeight: 14,
  }
}

function createNamedPointLabel(
  name: string | undefined,
  xMeasurement: GeckoMeasurement,
  yMeasurement: GeckoMeasurement,
  componentBounds: GeckoComponentRect
) {
  if (!name) {
    return undefined
  }

  const x = toCoordinateValue(xMeasurement, componentBounds.x.unit)
  const y = toCoordinateValue(yMeasurement, componentBounds.y.unit)
  if (!isFiniteCoordinate(x) || !isFiniteCoordinate(y)) {
    return undefined
  }

  return {
    x,
    y,
    lines: [name],
    anchor: LabelAnchor.Center,
    placement: LabelPlacement.Anchored,
    priority: 3,
    font: '12px Arial',
    lineHeight: 14,
  }
}

function createCoordinateFeatureLabel(
  name: string | undefined,
  coordinates: readonly CoordinatePair[],
  componentBounds: GeckoComponentRect
) {
  if (!name) {
    return undefined
  }

  const points = projectCoordinatePairs(coordinates, componentBounds)
  if (points.length === 0) {
    return undefined
  }

  const coordinateTotal = points.reduce(
    (total, point) => ({
      x: total.x + point.x,
      y: total.y + point.y,
    }),
    { x: 0, y: 0 }
  )

  return {
    x: coordinateTotal.x / points.length,
    y: coordinateTotal.y / points.length,
    lines: [name],
    anchor: LabelAnchor.Center,
    placement: LabelPlacement.Anchored,
    priority: 3,
    font: '12px Arial',
    lineHeight: 14,
  }
}

function getPointFeatureLabelOffset(
  feature:
    | GeckoNozzle
    | GeckoManway
    | GeckoRectangularManway
    | GeckoLeakDetectionValve
    | GeckoFixedSensor,
  unit: GeckoMeasurementUnit
) {
  const inch = GeckoMeasurement.CreateFromValue(1, GeckoMeasurementUnit.Inches)

  switch (feature.type) {
    case GeckoFeatureModelType.Nozzle:
      return toCoordinateValue(feature.radius.plus(inch), unit)
    case GeckoFeatureModelType.Manway:
      return toCoordinateValue(
        feature.radius.scale(FEATURE_REPAD_SCALE).plus(inch),
        unit
      )
    case GeckoFeatureModelType.RectangularManway:
      return toCoordinateValue(feature.height.plus(inch), unit)
    case GeckoFeatureModelType.LeakDetectionValve:
      return toCoordinateValue(feature.radius.plus(inch), unit)
    case GeckoFeatureModelType.FixedSensor:
      return toCoordinateValue(
        GeckoMeasurement.CreateFromValue(3, GeckoMeasurementUnit.Inches),
        unit
      )
  }
}

function createPlateFeatureLabel(
  plate: GeckoPlate,
  componentBounds: GeckoComponentRect
) {
  if (!plate.name) {
    return undefined
  }

  const geometry = plate.geometry.project(componentBounds.space)
  const xMin = geometry.x.min.num
  const xMax = geometry.x.max.num
  const yMin = geometry.y.min.num
  const yMax = geometry.y.max.num

  if (
    !isFiniteCoordinate(xMin) ||
    !isFiniteCoordinate(xMax) ||
    !isFiniteCoordinate(yMin) ||
    !isFiniteCoordinate(yMax)
  ) {
    return undefined
  }

  return {
    x: (xMin + xMax) / 2,
    y: (yMin + yMax) / 2,
    lines: [plate.name],
    anchor: LabelAnchor.Center,
    placement: LabelPlacement.Anchored,
    priority: 3,
    font: '12px Arial',
    lineHeight: 14,
  }
}

function createPolygonalPlateFeatureLabel(
  plate: GeckoPolygonalPlate,
  componentBounds: GeckoComponentRect
) {
  if (!plate.name) {
    return undefined
  }

  const points = plate.geometry
    .map((point) => ({
      x: toCoordinateValue(point.x, componentBounds.x.unit),
      y: toCoordinateValue(point.y, componentBounds.y.unit),
    }))
    .filter(
      (point) => isFiniteCoordinate(point.x) && isFiniteCoordinate(point.y)
    )

  if (points.length === 0) {
    return undefined
  }

  const coordinateTotal = points.reduce(
    (total, point) => ({
      x: total.x + point.x,
      y: total.y + point.y,
    }),
    { x: 0, y: 0 }
  )

  return {
    x: coordinateTotal.x / points.length,
    y: coordinateTotal.y / points.length,
    lines: [plate.name],
    anchor: LabelAnchor.Center,
    placement: LabelPlacement.Anchored,
    priority: 3,
    font: '12px Arial',
    lineHeight: 14,
  }
}

function createWeldFeatureLabel(
  weld: ReportWeldFeature,
  componentBounds: GeckoComponentRect
) {
  if (!weld.name) {
    return undefined
  }

  const xStart = toCoordinateValue(
    weld.feature_geometry.x.start,
    componentBounds.x.unit
  )
  const xEnd = toCoordinateValue(
    weld.feature_geometry.x.end,
    componentBounds.x.unit
  )
  const yStart = toCoordinateValue(
    weld.feature_geometry.y.start,
    componentBounds.y.unit
  )
  const yEnd = toCoordinateValue(
    weld.feature_geometry.y.end,
    componentBounds.y.unit
  )

  if (
    !isFiniteCoordinate(xStart) ||
    !isFiniteCoordinate(xEnd) ||
    !isFiniteCoordinate(yStart) ||
    !isFiniteCoordinate(yEnd)
  ) {
    return undefined
  }

  const isMostlyVertical = Math.abs(yEnd - yStart) >= Math.abs(xEnd - xStart)
  return {
    x: isMostlyVertical ? xEnd : (xStart + xEnd) / 2,
    y: isMostlyVertical ? (yStart + yEnd) / 2 : yEnd,
    lines: [weld.name],
    anchor: isMostlyVertical ? LabelAnchor.Bottom : LabelAnchor.Left,
    placement: LabelPlacement.Anchored,
    priority: 2,
    font: '12px Arial',
    lineHeight: 14,
  }
}

function createCourseLabelLines(course: CourseWithThickness) {
  const lines = [course.name]
  if (course.wallNominalThickness) {
    lines.push(
      `Nominal: ${formatMeasurementLabel(course.wallNominalThickness)}`
    )
  }
  return lines
}

function formatMeasurementLabel(measurement: GeckoMeasurement) {
  const unit = resolveValueDisplayUnit(measurement.unit)
  return `${formatTick(toCoordinateValue(measurement, unit))} ${formatUnitLabel(unit)}`
}

function formatTubeRange(wall: BoilerWall) {
  const start = Math.round(wall.customerGeometry.x.min.num)
  const end = Math.round(wall.customerGeometry.x.max.num)
  if (!Number.isFinite(start) || !Number.isFinite(end)) {
    return undefined
  }
  return `Tubes ${start}-${end}`
}

function formatUnitLabel(unit: GeckoMeasurementUnit) {
  switch (unit) {
    case GeckoMeasurementUnit.Feet:
      return 'ft'
    case GeckoMeasurementUnit.Inches:
      return 'in'
    case GeckoMeasurementUnit.Meters:
      return 'm'
    case GeckoMeasurementUnit.Millimeters:
      return 'mm'
    case GeckoMeasurementUnit.Degrees:
      return 'deg'
    default:
      return unit.id
  }
}

export function toCoordinateValue(
  measurement: GeckoMeasurement,
  unit: GeckoMeasurementUnit
) {
  try {
    return measurement.toNumber(unit)
  } catch {
    return measurement.num
  }
}

export function roundForKey(value: number) {
  return Number(value.toFixed(6))
}

function isFiniteCoordinate(value: number) {
  return Number.isFinite(value)
}
